import { Box } from '@mui/material';
import { Header } from 'components/home';
import { StepperHorizontal } from 'components/shared/stepper_horizontal';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import React, { useEffect, useState } from 'react';
import Step1 from './Step1';
import Step2, { buildPayload } from './Step2';
import useCifaStore from 'stores/cifa/cifa.store';
import { ROUTES } from 'routes/constants';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import schoolFeedingValidationSchema from './schoolFeeding.validation';
import {
  createDefaultSections,
  resolveInitialSections,
  convertToApiFormat,
  extractYearFromPath,
} from './schoolFeeding.helpers';

export interface InstitutionI {
  id: number;
  name: string;
}

interface Produto {
  id: string;
  nomeRazao: string;
  documento: string;
  valor: string;
  valorNumerico: number;
}

interface SecaoAquisicao {
  titulo: string;
  produtosCnpj: Produto[];
  produtosCpf: Produto[];
}

const SchoolFeeding2026: React.FC = () => {
  const steps = ['Alimentação Escolar', 'Confirmação dos Dados'];
  const [activeStep, setActiveStep] = useState<number>(0);
  const [submittedOnce, setSubmittedOnce] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { cifa, setPartialForm } = useCifaStore();

  // initial sections logic moved to helpers

  const formik = useFormik({
    initialValues: {
      alimentacao_af_recurso_pnae: cifa.alimentacao_af_recurso_pnae || 0,
      alimentacao_af_recurso_estado: cifa.alimentacao_af_recurso_estado || 0,
      alimentacao_af_recurso_prefeitura: cifa.alimentacao_af_recurso_prefeitura || 0,
      alimentacao_total_recurso_pnae: cifa.alimentacao_total_recurso_pnae || 0,
      alimentacao_total_recurso_estado: cifa.alimentacao_total_recurso_estado || 0,
      alimentacao_total_recurso_prefeitura: cifa.alimentacao_total_recurso_prefeitura || 0,
      alimentacao_ata_aprovacao_uuid: cifa.alimentacao_ata_aprovacao_uuid ?? '',
      secoesAquisicao: resolveInitialSections(cifa, cifa.uuid ? localStorage.getItem(`schoolFeeding_secoes_${cifa.uuid}`) : null)
    },
    validationSchema: schoolFeedingValidationSchema,
    onSubmit: (values) => {
      if (activeStep === 0) {
        setActiveStep((prevStep) => prevStep + 1);
      }
      if (activeStep === 1) {
        // Lógica para finalizar ou salvar
        console.log('Formulário finalizado:', values);
      }
    },
    validateOnChange: submittedOnce,
    validateOnBlur: submittedOnce,
  })

  const anoReferencia = extractYearFromPath(location.pathname, 2026);

  // convertToApiFormat available from helpers

  const handleNext = async () => {
    setSubmittedOnce(true);

    // Salvar os dados das aquisições no store antes de avançar
    if (formik.values.secoesAquisicao && formik.values.secoesAquisicao.length >= 3) {
      const payload = buildPayload(formik.values.secoesAquisicao);
      setPartialForm('aquisicoes_pnae_federal_detalhamento', payload.aquisicoes_pnae_federal_detalhamento);
      setPartialForm('aquisicoes_governo_estado_detalhamento', payload.aquisicoes_governo_estado_detalhamento);
      setPartialForm('aquisicoes_prefeitura_detalhamento', payload.aquisicoes_prefeitura_detalhamento);
    }

    formik.handleSubmit();
  };

  const handleBack = () => setActiveStep((prevStep) => prevStep - 1);

  useEffect(() => {
    if (!cifa.uuid) {
      navigate(ROUTES[`CIFA${anoReferencia}` as keyof typeof ROUTES] as string);
    }
  }, [cifa, navigate, anoReferencia]);

  // Recarregar dados quando o CIFA mudar (apenas uma vez ao montar)
  useEffect(() => {
    if (cifa.uuid && !formik.values.secoesAquisicao) {
      const updatedSections = resolveInitialSections(cifa, cifa.uuid ? localStorage.getItem(`schoolFeeding_secoes_${cifa.uuid}`) : null);
      formik.setFieldValue('secoesAquisicao', updatedSections);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Salvar seções no localStorage quando houver mudanças (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formik.values.secoesAquisicao && formik.values.secoesAquisicao.length > 0 && cifa.uuid) {
        const hasData = formik.values.secoesAquisicao.some(secao =>
          secao.produtosCnpj.length > 0 || secao.produtosCpf.length > 0
        );

        if (hasData) {
          localStorage.setItem(
            `schoolFeeding_secoes_${cifa.uuid}`,
            JSON.stringify(formik.values.secoesAquisicao)
          );
        }
      }
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [formik.values.secoesAquisicao, cifa.uuid]);

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title={`CIFA - Cadastrar Município - ${anoReferencia}`} showBackButton={true} />
      <Box width='97%' display='flex' flexDirection='column' justifyContent='center' alignItems='center' margin='2rem'>
        <StepperHorizontal activeStep={activeStep} steps={steps} maxWidth='1152px' />
        {activeStep === 0 && (
          <Step1
            initialData={formik.values.secoesAquisicao || resolveInitialSections(cifa, cifa.uuid ? localStorage.getItem(`schoolFeeding_secoes_${cifa.uuid}`) : null)}
            onNext={handleNext}
            formik={formik}
          />
        )}
        {activeStep === 1 && <Step2 formik={formik} onBack={handleBack} />}
      </Box>
    </MenuProvider>
  );
};

export default SchoolFeeding2026;
